import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Skeleton,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { useAppSelector, useAppDispatch } from 'app/store';
import { fetchCreditsDetailsEffect } from 'app/store/Contributions/effects';
import { Credit } from 'types/api.types';
import { getCreditsDetails } from 'app/store/Contributions/selectors';
import { useIsMounted } from 'app/utils/hooks';
import { Contribution, ContributionClassRolesDDL } from 'types/contribution';
import { getChipRoleLabel } from 'app/components/Song/Contributions/ContributionsList/utils';
import { dateFormats } from 'app/constants';
import { TableList, ListItemText } from 'app/components/ChakraTableList';

interface CreditsListDialogComponentProps {
  onClose: () => void;
  isOpen: boolean;
  isLoading: boolean;
  creditsDetails?: Contribution[];
  title: string;
  contributionRoles?: ContributionClassRolesDDL;
}

export const CreditsListDialogComponent: React.FC<CreditsListDialogComponentProps> = ({
  isOpen,
  onClose,
  creditsDetails = [],
  title,
  contributionRoles,
  isLoading,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    size="lg"
    data-testid="roles-list-dialog"
    scrollBehavior="inside"
  >
    <ModalOverlay />
    <ModalContent maxHeight="full">
      <ModalHeader>
        <Text
          as="h2"
          textOverflow="ellipsis"
          overflow="hidden"
          fontSize="lg"
          fontWeight={600}
          lineHeight="small"
          letterSpacing="0.15px"
          data-testid="dialog-title"
          title={title}
        >
          {title}
        </Text>
      </ModalHeader>
      <ModalBody data-testid="dialog-content">
        <TableList
          loadingOptions={{
            loading: isLoading,
            repeat: 5,
            loader: {
              role: <Skeleton width="150px" height={6} />,
              dateAdded: <Skeleton width="150px" height={6} />,
              location: <Skeleton width="150px" height={6} />,
              country: <Skeleton width="70px" height={6} />,
              addedBy: <Skeleton width="100px" height={6} />,
            },
          }}
          columns={[
            { key: 'role', name: 'Role' },
            { key: 'dateAdded', name: 'Date Added' },
            { key: 'location', name: 'Location' },
            { key: 'country', name: 'Country' },
            { key: 'addedBy', name: 'Added By' },
          ]}
          rows={creditsDetails?.map((contribution) => ({
            role: (
              <ListItemText
                data-testid="credits-list-role"
                primary={getChipRoleLabel(contribution, contributionRoles)}
                title={getChipRoleLabel(contribution, contributionRoles)}
              />
            ),
            dateAdded: (
              <ListItemText
                data-testid="credits-list-date-added"
                primary={
                  contribution.date?.date
                    ? format(
                        new Date(contribution.date?.date),
                        dateFormats.LIST_FORMAT
                      )
                    : '-'
                }
              />
            ),
            location: (
              <ListItemText
                data-testid="credits-list-location"
                primary={contribution.location?.name || '-'}
                title={contribution.location?.name}
              />
            ),
            country: (
              <ListItemText
                data-testid="credits-list-country"
                primary={contribution.location?.country.countryCode || '-'}
              />
            ),
            addedBy: (
              <ListItemText
                data-testid="credits-list-added-by"
                primary={contribution.createdBy.profileCard.displayName}
                title={contribution.createdBy.profileCard.displayName}
              />
            ),
          }))}
        />
      </ModalBody>
      <ModalFooter data-testid="dialog-actions">
        <Button onClick={onClose}>Done</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

interface CreditsListDialogProps {
  onClose: () => void;
  isOpen: boolean;
  credit: Credit;
  title: string;
  contributionRoles?: ContributionClassRolesDDL;
}

export const CreditListDialog: React.FC<CreditsListDialogProps> = ({
  isOpen,
  onClose,
  credit,
  title,
  contributionRoles,
}) => {
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const creditsDetails = useAppSelector((state) =>
    getCreditsDetails(state, credit.guid)
  );

  const fetchDetails = async () => {
    try {
      setIsLoading(true);
      await dispatch(fetchCreditsDetailsEffect(credit)).unwrap();
      if (isMounted()) {
        setIsLoading(false);
      }
    } catch {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <CreditsListDialogComponent
      onClose={onClose}
      isOpen={isOpen}
      isLoading={isLoading}
      // @ts-ignore
      creditsDetails={creditsDetails.items}
      title={title}
      contributionRoles={contributionRoles}
    />
  );
};
